import project1 from '@/shared/assets/education/portfolio/project1.png';
import aiSlideBasics from '@/shared/assets/education/programs/blog-1.png';
import aiSlideDots from '@/shared/assets/education/programs/blog-2.png';
import aiClassPhoto from '@/shared/assets/education/programs/blog-3.jpg';

import type { PortfolioProject } from '../model/portfolio.schema';

/**
 * ⚠️ `○○` 로 남은 숫자는 실제 값으로 채워야 한다.
 * 「지도를 만들었다」보다 「상권 120곳이 등록됐다」가 훨씬 세다.
 */
export const PORTFOLIO: PortfolioProject[] = [
  {
    id: 'paichai-ro',
    badge: '대학·공공 지역 프로젝트 1호',
    // 프로그램명에 「지도」를 넣지 않는다 — 1호의 결과물이 지도였을 뿐이다
    title: '지역 정보를 한 장의 지도로',
    headline: '학생이 직접 채우는 지역 지도, 배재로',
    outcome: '학생들이 직접 조사해 모은 데이터 100여 건',
    caption: 'AI 교육 + 플랫폼 구축',
    thumb: project1,
    thumbKind: 'cover',
    thumbAlt: '배재로 브랜드 키비주얼 — Find your place 문구와 컬러 도형들',
    inquiry: {
      label: '교육·도입 문의하기',
      to: '/education/inquiry?type=institution',
    },
    sections: [
      {
        id: 'why-now',
        heading: '왜 지금, 대학가 상권인가',
        paragraphs: [
          '대학 인근 상권은 다른 지역 상권과 구조가 다릅니다. **매출의 상당 부분이 학생 인구에 의존합니다.** 그런데 그 전제가 흔들리고 있습니다. 학령인구가 줄면서 신입생 수 자체가 감소하고, 상주하는 학생 수가 매년 줄어드는 대학이 늘고 있습니다.',
          '이 상황에서 상권 정보를 지도에 올리는 일은 「편의를 더하는」 수준의 서비스가 아닙니다. 상권이 외부에 발견될 수 있는 몇 안 되는 통로 중 하나를 만드는 일에 가깝습니다. **정보가 없으면 존재해도 발견되지 않고, 발견되지 않으면 신규 유입 소비로 이어지지 않습니다.** 배재로는 이 지점에서 출발했습니다.',
        ],
      },
      {
        id: 'what-we-do',
        heading: '우리가 할 수 있는 일',
        paragraphs: [
          '지역 상권의 상당수는 소상공인이 운영합니다. 좋은 공간, 좋은 메뉴가 있어도 **이를 온라인에 꾸준히 노출시킬 인력과 자원은 대부분 없습니다.** SNS 계정 하나 운영할 여력조차 없는 곳도 많습니다.',
          '배재로가 하려는 일은 이 격차를 메우는 것입니다. **상권을 대신 홍보해주는 대행사가 아니라, 상권이 발견될 수 있는 최소한의 창구를 만들고 그 창구가 꺼지지 않도록 계속 채워지게 하는 구조를 만드는 일입니다.** 우리가 지도를 만든다고 상권이 살아나지는 않습니다. 다만 상권이 사라지는 속도를 늦추는 데는 분명히 기여할 수 있다고 봤습니다.',
        ],
      },
      {
        id: 'newcomers',
        heading: '타지에서 온 사람들에게 필요한 지도',
        paragraphs: [
          '대학가에는 그 지역에 연고가 없는 사람들이 매년 새로 들어옵니다. **신입생, 편입생, 그리고 유학생입니다.** 이들은 지역 정보를 얻을 통로가 원래 살던 지역 학생들보다 훨씬 부족합니다. 아는 선배도 없고, 동네 정보를 물어볼 가족도 근처에 없습니다.',
          '이 인구가 지역 상권을 발견하고 실제로 소비하게 만드는 것 자체가 상권 활성화에 직접적으로 기여하는 일입니다. 배재로가 신경 쓴 지점은 「학생이 편하게 찾도록 돕는다」가 아니라, **「외부에서 유입된 소비력을 지역 상권으로 연결하는 채널을 만든다」**는 것이었습니다. 지도 위에 학교와의 거리, 최근 등록순 같은 정렬 기준을 둔 것도 이 때문입니다. 지역을 잘 모르는 사람이 가장 먼저 필요로 하는 기준이 그것이었습니다.',
        ],
      },
      {
        id: 'why-students',
        heading: '왜 학생이 직접 채워야 했나',
        paragraphs: [
          '답은 외부 업체도, 학교 행정팀도 아니었습니다. **그 동네에 실제로 살고, 그 가게를 실제로 다니는 학생과 주민이었습니다.** 외부인이 조사해서 만든 정보는 개업·폐업이 반영되지 않으면 금방 낡습니다. 반면 그 지역에 실제로 거주하는 사람이 채우는 정보는 상권의 진짜 특징, 그러니까 어느 가게가 학생들 사이에서 실제로 인기 있는지, 어떤 곳이 최근에 생겼는지를 현장감 있게 담아냅니다.',
          '그래서 정리된 결과물을 넘기는 방식이 아니라, **정보가 계속 채워지는 자리를 만드는 쪽**으로 방향을 잡았습니다. 그리고 그 조사하고 등록하는 과정 자체를 교육으로 만들었습니다. **학생들에게 가르친 것은 도구 사용법이 아니라 순서였습니다.** 무엇을 조사할지 먼저 정하고, 같은 기준으로 기록하고, 남이 알아볼 수 있게 남기는 것. 이 기준이 흔들리지 않아야 데이터가 쌓일수록 지도의 쓸모가 커집니다.',
        ],
      },
      {
        id: 'ai-class',
        heading: 'AI 교육도 함께 진행했습니다',
        media: [
          {
            kind: 'image',
            src: aiClassPhoto,
            alt: '회의실에서 수업 중인 모습 — 화면에 「말로 안 되면, 사진을 보여주세요」 슬라이드가 떠 있다',
            caption: '수업 현장',
            // 위쪽 빈 벽과 앞쪽 빈 테이블이 넓다. 화면과 사람이 있는 가운데 띠만 남긴다
            crop: { aspect: '16 / 7', focus: '50% 52%' },
            before: 1,
          },
          {
            kind: 'image',
            src: aiSlideBasics,
            alt: '수업 슬라이드 — LLM은 대규모 언어 모델이고, 질문 뒤에 올 가장 그럴듯한 말을 이어붙인다는 설명',
            caption: '수업 자료 — LLM',
            before: 3,
          },
          {
            kind: 'image',
            src: aiSlideDots,
            alt: '수업 슬라이드 — AI의 머릿속을 점으로 그리고, 질문이 찍힌 점 근처를 이어 답을 만든다는 그림',
            caption: '수업 자료 — AI의 머릿속',
            before: 4,
          },
        ],
        paragraphs: [
          '학생들이 지역 콘텐츠만 채운 것이 아닙니다. **이 프로젝트는 처음부터 AI 교육과 함께 갔습니다.** 조사하고 등록하는 법을 가르치면서 생성형 AI 쓰는 법을 나란히 다뤘고, 지도는 그 둘이 같이 굴러가면서 채워졌습니다.',
          'AI는 계속 바뀝니다. **당장 쓰는 법만 알려주면 그 지식은 금방 낡습니다.** 학생들이 앞으로의 변화를 스스로 따라잡을 수 있도록 **원리 강의부터 활용법 강의까지 이어서** 진행했습니다.',
          '원리는 두 갈래로 나눠 설명했습니다. **글을 만드는 LLM**과 **이미지를 만드는 디퓨전**입니다. 둘 다 학습해 둔 것을 바탕으로 그럴듯한 결과를 만들어 낸다는 점은 같습니다.',
          'LLM은 정답을 아는 기계가 아니라, **질문 뒤에 올 가장 그럴듯한 말을 이어붙이는 기계입니다.** 그래서 배운 적 없는 것을 물으면 모른다고 하지 않고 그럴듯하게 지어냅니다. 이걸 모르면 지어낸 답을 사실로 옮겨 적게 됩니다.',
          'AI의 머릿속은 학습해 둔 지식이 찍힌 점들입니다. 질문이 들어오면 그 근처의 점을 이어 다음 말을 만듭니다. **최신 정보와 내부 사정은 그 점 안에 없습니다.** 어느 가게가 지난달에 문을 닫았는지, 요즘 학생들이 어디에 몰리는지는 학습 데이터에 있을 리가 없습니다.',
          '이 두 가지를 알고 나면 도구가 바뀌어도 옮겨 탈 수 있습니다. 그 위에서 **ChatGPT, Claude, Gemini, Genspark, Grok** 을 놓고 활용법을 다뤘습니다. 같은 일을 시킬 때 무엇을 어떻게 물어야 원하는 답이 나오는지를 도구마다 확인했습니다.',
          '묻는 방법 중 하나가 **사진을 붙이는 것**입니다. 원하는 분위기, 원하는 구도, 실제 가게 사진을 붙여 물으면 말로만 설명할 때보다 결과가 정확해집니다. 남의 사진을 참고할 때는 저작권을 확인해야 한다는 것도 짚었습니다.',
          '**AI 계정도 학생들에게 넘겼습니다.** 수업이 끝난 뒤에도 조사하고 등록하는 내내 직접 쓰면서 익히도록 했습니다.',
          '다만 AI를 아무리 잘 써도 **지역 상권 정보는 대신 채워 주지 못합니다.** 그 동네에 사는 사람이 직접 확인해 알려줘야 정확한 답이 나옵니다.',
        ],
      },
      {
        id: 'features',
        heading: '배재로에 있는 것',
        // 「예쁘다」로 읽히는 키비주얼 대신 실제로 도는 화면을 먼저 보여준다
        media: [
          {
            kind: 'video',
            src: '/media/paichairo-ro.mp4',
            caption: '배재로 실제 화면',
            before: 1,
          },
        ],
        paragraphs: [
          '학생들이 직접 조사해 올린 데이터가 **100여 건** 모였습니다.',
          '모인 데이터는 네 곳에서 보입니다.',
        ],
        features: [
          {
            name: '지도',
            url: 'https://paichairo.com/map',
            description: '학생들이 등록한 상권. 학교와의 거리, 최근 등록순으로 정렬합니다.',
          },
          {
            name: '코스',
            url: 'https://paichairo.com/stories',
            description: '학생들이 직접 짠 동네 코스.',
          },
          {
            name: '이벤트',
            url: 'https://paichairo.com/events',
            description: '진행 중인 지역 이벤트.',
          },
          {
            name: '랭킹',
            url: 'https://paichairo.com/ranking',
            description: '등록이 활발한 순으로 봅니다.',
          },
        ],
      },
      {
        id: 'next',
        heading: '앞으로의 방향',
        paragraphs: [
          '학령인구 감소는 대부분의 지역 대학이 함께 겪는 흐름이고, 대학가 상권이 신입생·편입생·유학생에게 발견되지 못하는 것도 특정 지역만의 문제가 아닙니다.',
          '앞으로 더 많은 대학, 지자체와 함께 이 구조를 넓혀 가려 합니다. 지역마다 상권이 스스로 발견될 창구를 하나씩 만들고, 그것이 지역 경제가 버티는 데 보탬이 되는 것까지가 목표입니다.',
        ],
      },
    ],
  },
];
